import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Radio, Video, FileText, CalendarDays, FolderOpen, ArrowRight, Lock, Users,
  Bell, Trash2, Pencil, Upload, GraduationCap,
} from "lucide-react";

const SLOTS = [
  { key: "live", num: "01", name: "LIVE", desc: "Go live and connect with your clients in real-time.", cta: "Open Live", icon: Radio, enabled: true },
  { key: "e_video", num: "02", name: "E-VIDEO", desc: "Share educational video (via Telegram link) for your clients.", cta: "Open E-Video", icon: Video, enabled: true },
  { key: "e_book", num: "03", name: "E-BOOK", desc: "Upload and share PDF materials to educate your clients.", cta: "Open E-Book", icon: FileText, enabled: true },
  { key: "webinar", num: "04", name: "WEBINAR", desc: "Host webinars with student capacity — free seats (IB-approved) or instant COINS booking.", cta: "Open Webinar", icon: CalendarDays, enabled: true },
  { key: "resources", num: "05", name: "RESOURCES", desc: "Share Indicators and Expert Advisors with permanent, monthly or weekly access.", cta: "Open Resources", icon: FolderOpen, enabled: true },
  { key: "coaching", num: "06", name: "PERSONAL COACHING", desc: "Paid 1-on-1 coaching bookings with your own price, availability and capacity.", cta: "Open Coaching", icon: GraduationCap, enabled: true },
];

const SLOT_TITLES = { webinar: "WEBINAR", coaching: "PERSONAL COACHING", resource_indicator: "RESOURCE — INDICATOR", resource_ea: "RESOURCE — EXPERT ADVISOR" };
const SLOT_TYPE = { coaching: "personal_coaching" }; // slot key -> activity type

const LBL = "text-[10px] font-mono uppercase tracking-widest text-[#64748B] block mb-1.5";

import { fmtMYT, fmtMYTDate } from "@/lib/myt";
const fmtDT = (v) => fmtMYT(v);
const fmtDate = (v) => fmtMYTDate(v);
const toLocalInput = (v) => { if (!v) return ""; const d = new Date(v); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const toDateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");

const lifecycleOf = (a) => {
  if (a.type !== "live") return a.status === "published" ? "PUBLISHED" : "DRAFT";
  return a.lifecycle || (a.status === "published" ? "ACTIVE" : "DRAFT");
};
const badgeCls = (s) =>
  s === "ACTIVE" || s === "PUBLISHED"
    ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10"
    : s === "EXPIRED"
      ? "text-[#64748B] border-white/10 bg-white/5"
      : "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10";

function Field({ label, children }) {
  return <div><label className={LBL}>{label}</label>{children}</div>;
}

function AccessPicker({ f, setModal }) {
  return (
    <>
      <Field label="Access">
        <div className="flex gap-2" data-testid="access-picker">
          {[["free", "FREE"], ["coin", "PAID · COINS"]].map(([v, label]) => (
            <button type="button" key={v} data-testid={`access-${v}`}
              onClick={() => setModal((m) => ({ ...m, form: { ...m.form, access_type: v } }))}
              className={f.access_type === v
                ? "px-4 py-2 rounded-full text-xs font-mono tracking-wider border border-[#D4AF37]/60 bg-[#D4AF37]/15 text-[#D4AF37] transition-colors"
                : "px-4 py-2 rounded-full text-xs font-mono tracking-wider border border-white/10 text-[#94A3B8] hover:border-[#D4AF37]/40 transition-colors"}>
              {label}
            </button>
          ))}
        </div>
      </Field>
      {f.access_type === "coin" && (
        <Field label="COINS price (1 COINS = USD 1)">
          <input type="number" min="1" step="0.01" value={f.coin_price}
            onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, coin_price: e.target.value } }))}
            placeholder="e.g. 100" className="input-lux text-sm font-mono" data-testid="coin-price-input" />
        </Field>
      )}
    </>
  );
}

function Modal({ title, sub, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-lux w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <p className="eyebrow-label mb-1">{title}</p>
        {sub && <p className="text-xs text-[#64748B] mb-6">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

const EMPTY_FORMS = {
  live: () => ({ title: "", starts_at: "", duration_hours: "1", live_url: "", publish: true }),
  e_video: () => ({ title: "", content: "", external_url: "", media_url: "", access_type: "free", coin_price: "", expires_at: "", publish: true }),
  e_book: () => ({ title: "", content: "", file_path: "", file_name: "", media_url: "", access_type: "free", coin_price: "", expires_at: "", publish: true }),
  webinar: () => ({ title: "", content: "", event_date: "", event_time: "", media_url: "", capacity: "", access_type: "free", coin_price: "", publish: true }),
  coaching: () => ({ title: "", content: "", media_url: "", location: "", availability: "", capacity: "", coin_price: "", publish: true }),
  resource_indicator: () => ({ title: "", content: "", media_url: "", validity: "permanent", tutorial_url: "", requirements: "", access_type: "free", coin_price: "", publish: true }),
  resource_ea: () => ({ title: "", content: "", media_url: "", file_path: "", file_name: "", validity: "permanent", tutorial_url: "", requirements: "", access_type: "free", coin_price: "", publish: true }),
};

export default function IBActivity() {
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);
  const [modal, setModal] = useState(null); // {slot, id|null, form}
  const [resTab, setResTab] = useState("indicator");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bookAct, setBookAct] = useState(null); // activity whose bookings are being managed
  const [bookRows, setBookRows] = useState(null);
  const [rejId, setRejId] = useState(null);
  const [rejReason, setRejReason] = useState("");

  useEffect(() => {
    const slot = searchParams.get("slot");
    if (slot) setSelected(slot);
  }, [searchParams]);

  const openBookings = (a) => {
    setBookAct(a); setBookRows(null); setRejId(null); setRejReason("");
    api.get(`/ib/activity/${a.id}/bookings`).then((r) => setBookRows(r.data.bookings || [])).catch(() => setBookRows([]));
  };
  const decideBooking = async (bid, action, reason = "") => {
    try {
      await api.post(`/ib/activity/bookings/${bid}/decision`, { action, reason });
      toast.success(action === "approve" ? "Booking approved — client notified" : "Booking rejected — client notified");
      setRejId(null); setRejReason("");
      openBookings(bookAct);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  // Indicator Requests — operational view of client claims/purchases of THIS IB's indicators
  const [indRequests, setIndRequests] = useState(null);
  const [reqDetail, setReqDetail] = useState(null);
  const loadIndRequests = () => {
    setIndRequests(null);
    api.get("/ib/activity/indicator-requests").then((r) => setIndRequests(r.data.requests || [])).catch(() => setIndRequests([]));
  };
  const markReqDone = async (id) => {
    try {
      await api.post(`/ib/activity/indicator-requests/${id}/status`, { status: "done" });
      toast.success("Request marked as done");
      setReqDetail(null);
      loadIndRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };
  useEffect(() => { if (selected === "resources" && resTab === "requests") loadIndRequests(); }, [selected, resTab]);

  const fileRef = useRef(null);

  const load = () => {
    api.get("/ib/activity").then((r) => setActivities(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const byType = (t) => activities.filter((a) => a.type === t);

  const openCreate = (slot) => setModal({ slot, id: null, form: EMPTY_FORMS[slot]() });
  const openEdit = (a) => {
    if (a.type === "live") {
      setModal({ slot: "live", id: a.id, form: { title: a.title, starts_at: toLocalInput(a.starts_at), duration_hours: String((a.duration_minutes || 60) / 60), live_url: a.live_url || "", publish: false } });
    } else {
      setModal({ slot: a.type === "personal_coaching" ? "coaching" : a.type, id: a.id, form: { title: a.title, content: a.content || "", external_url: a.external_url || "", file_path: "", file_name: a.file_name || "", access_type: a.access_type || "free", coin_price: a.coin_price_minor ? String(a.coin_price_minor / 100) : "", expires_at: toDateInput(a.expires_at), media_url: a.media_url || "", capacity: a.capacity ? String(a.capacity) : "", event_date: a.event_date || "", event_time: a.event_time || "", location: a.location || "", availability: a.availability || "", validity: a.validity || "permanent", tutorial_url: a.tutorial_url || "", requirements: a.requirements || "", publish: false } });
    }
  };

  const buildBody = () => {
    const { slot, form, id } = modal;
    if (!form.title.trim()) throw new Error("Title is required");
    if (slot === "live") {
      if (!form.starts_at) throw new Error("Start date/time is required");
      if (!form.live_url.trim()) throw new Error("Join URL is required");
      return {
        type: "live", title: form.title, live_url: form.live_url.trim(), live_platform: "other",
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: Math.max(1, Math.round(parseFloat(form.duration_hours || "1") * 60)),
        publish: !!form.publish,
      };
    }
    const base = {
      type: slot, title: form.title, content: form.content || "",
      eligible_plans: [],
      expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      publish: !!form.publish,
    };
    if (slot === "e_video" || slot === "e_book") {
      base.media_url = form.media_url || "";
      base.access_type = form.access_type || "free";
      if (base.access_type === "coin") {
        const price = Math.round(parseFloat(form.coin_price || "0") * 100);
        if (!Number.isFinite(price) || price <= 0) throw new Error("A COINS price greater than zero is required for paid content");
        base.coin_price_minor = price;
      }
    }
    if (slot === "webinar") {
      base.capacity = parseInt(form.capacity || "0", 10);
      if (!Number.isFinite(base.capacity) || base.capacity < 1) throw new Error("Student capacity is required");
      base.event_date = form.event_date || "";
      base.event_time = form.event_time || "";
      base.media_url = form.media_url || "";
      base.access_type = form.access_type || "free";
      if (base.access_type === "coin") {
        const price = Math.round(parseFloat(form.coin_price || "0") * 100);
        if (!Number.isFinite(price) || price <= 0) throw new Error("A COINS price greater than zero is required for a paid webinar");
        base.coin_price_minor = price;
      }
    }
    if (slot === "coaching") {
      base.type = "personal_coaching";
      base.capacity = parseInt(form.capacity || "0", 10);
      if (!Number.isFinite(base.capacity) || base.capacity < 1) throw new Error("Capacity is required");
      base.location = form.location || "";
      base.availability = form.availability || "";
      base.media_url = form.media_url || "";
      const price = Math.round(parseFloat(form.coin_price || "0") * 100);
      if (!Number.isFinite(price) || price <= 0) throw new Error("A COINS price greater than zero is required for personal coaching");
      base.coin_price_minor = price;
      base.access_type = "coin";
    }
    if (slot === "resource_indicator" || slot === "resource_ea") {
      base.validity = form.validity || "permanent";
      base.tutorial_url = form.tutorial_url || "";
      base.requirements = form.requirements || "";
      base.media_url = form.media_url || "";
      base.access_type = form.access_type || "free";
      if (base.access_type === "coin") {
        const price = Math.round(parseFloat(form.coin_price || "0") * 100);
        if (!Number.isFinite(price) || price <= 0) throw new Error("A COINS price greater than zero is required for paid content");
        base.coin_price_minor = price;
      }
      if (slot === "resource_ea") {
        base.file_path = form.file_path || "";
        base.file_name = form.file_name || "";
      }
    }
    if (slot === "e_video") {
      if (!form.external_url.trim()) throw new Error("Telegram link is required");
      base.external_url = form.external_url.trim();
    }
    if (slot === "e_book") {
      if (!id && !form.file_path) throw new Error("Please upload the PDF first");
      if (form.file_path) { base.file_path = form.file_path; base.file_name = form.file_name || ""; }
    }
    return base;
  };

  const save = async () => {
    let body;
    try { body = buildBody(); } catch (err) { toast.error(err.message); return; }
    setSaving(true);
    try {
      if (modal.id) {
        await api.put(`/ib/activity/${modal.id}`, { ...body, publish: false });
        toast.success("Activity updated");
      } else {
        await api.post("/ib/activity", body);
        toast.success(body.publish ? "Published — eligible clients will be notified" : "Draft saved");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const uploadEa = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/ib/activity/resource-ea-upload", fd);
      setModal((m) => ({ ...m, form: { ...m.form, file_path: res.data.path, file_name: res.data.file_name } }));
      toast.success("EA file uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "EA upload failed");
    } finally {
      setUploading(false);
    }
  };
  const uploadImg = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/ib/activity/upload", fd);
      setModal((m) => ({ ...m, form: { ...m.form, media_url: res.data.url || "" } }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };
  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/ib/activity/ebook-upload", fd);
      setModal((m) => ({ ...m, form: { ...m.form, file_path: res.data.path, file_name: res.data.file_name } }));
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const metaLine = (a) => {
    if (a.type === "live") return `Start ${fmtDT(a.starts_at)} · ${a.duration_minutes ? `${a.duration_minutes / 60}h duration` : "no schedule"} · expires automatically`;
    if (a.type === "e_book") return `${a.file_name || "PDF"}${a.expires_at ? ` · expires ${fmtDate(a.expires_at)}` : ""} · created ${fmtDate(a.created_at)}`;
    if (a.type === "webinar") return `${[a.event_date, a.event_time].filter(Boolean).join(" ") || "date TBA"} · capacity ${a.capacity ?? "—"} · created ${fmtDate(a.created_at)}`;
    if (a.type === "personal_coaching") return `${[a.location, a.availability].filter(Boolean).join(" · ") || "availability TBA"} · capacity ${a.capacity ?? "—"} · created ${fmtDate(a.created_at)}`;
    if (a.type === "resource_indicator") return `Indicator · ${a.validity || "permanent"} · created ${fmtDate(a.created_at)}`;
    if (a.type === "resource_ea") return `Expert Advisor${a.file_name ? ` · ${a.file_name}` : ""} · ${a.validity || "permanent"} · created ${fmtDate(a.created_at)}`;
    return `${a.external_url || ""}${a.expires_at ? ` · expires ${fmtDate(a.expires_at)}` : ""} · created ${fmtDate(a.created_at)}`;
  };

  const renderRow = (a) => {
    const lc = lifecycleOf(a);
    return (
      <div key={a.id} className="card-lux p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`slot-item-${a.id}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium">{a.title}</span>
            <span className={`text-[9px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${badgeCls(lc)}`} data-testid={`slot-item-status-${a.id}`}>{lc}</span>
            {(a.type === "e_video" || a.type === "e_book" || a.type === "webinar" || a.type === "personal_coaching" || a.type === "resource_indicator" || a.type === "resource_ea") && (
              <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${a.access_type === "coin" ? "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10" : "text-emerald-400 border-emerald-400/30 bg-emerald-400/10"}`} data-testid={`slot-item-access-${a.id}`}>
                {a.access_type === "coin" ? `${(Number(a.coin_price_minor || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} COINS` : "FREE"}
              </span>
            )}
          </div>
          <p className="text-xs text-[#64748B] mt-1 truncate max-w-xl">{metaLine(a)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {a.status === "draft" && (
            <button onClick={() => act(() => api.post(`/ib/activity/${a.id}/publish`), "Published — eligible clients will be notified")}
              className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded px-3 py-1.5 transition-colors"
              data-testid={`slot-item-publish-${a.id}`}>Publish</button>
          )}
          {(a.type === "webinar" || a.type === "personal_coaching") && (
            <button onClick={() => openBookings(a)}
              className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-1.5 transition-colors"
              data-testid={`slot-item-bookings-${a.id}`}><span className="flex items-center gap-1"><Users className="h-3 w-3" />Bookings</span></button>
          )}
          <button onClick={() => openEdit(a)}
            className="text-xs text-[#94A3B8] border border-white/10 hover:bg-white/5 rounded px-3 py-1.5 transition-colors"
            data-testid={`slot-item-edit-${a.id}`}><span className="flex items-center gap-1"><Pencil className="h-3 w-3" />Edit</span></button>
          <button onClick={() => { if (window.confirm("Delete this activity?")) act(() => api.delete(`/ib/activity/${a.id}`), "Deleted"); }}
            className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded px-3 py-1.5 transition-colors"
            data-testid={`slot-item-delete-${a.id}`}><span className="flex items-center gap-1"><Trash2 className="h-3 w-3" />Delete</span></button>
        </div>
      </div>
    );
  };

  const slotMeta = SLOTS.find((s) => s.key === selected);
  const f = modal?.form;

  return (
    <div className="space-y-10" data-testid="ib-activity-page">
      {/* Header */}
      <div>
        <p className="eyebrow-label mb-2">MY ACTIVITY</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white">
          Create. <span className="text-gold-gradient">Share. Grow.</span>
        </h1>
        <p className="text-base text-[#94A3B8] mt-3 max-w-xl">Share valuable content with your clients and help them grow with you.</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-4 py-1.5" data-testid="push-notice">
          <Bell className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span className="text-xs text-[#D4AF37]/90">All activities will send push notification to your eligible clients.</span>
        </div>
      </div>

      {/* Module launcher — entry points only; create/manage lives INSIDE each module */}
      {!selected && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="activity-slots-grid">
        {SLOTS.map((s) => {
          const Icon = s.icon;
          const isSel = selected === s.key;
          return (
            <div key={s.key} data-testid={`slot-card-${s.key}`}
              onClick={() => s.enabled && setSelected(isSel ? null : s.key)}
              className={`card-lux relative p-8 flex flex-col items-center text-center gap-4 ${s.enabled ? "card-lux-hover cursor-pointer" : "opacity-60"} ${isSel ? "border-[#D4AF37]/60 shadow-[0_0_40px_rgba(212,175,55,0.12)]" : ""}`}>
              <span className="absolute top-5 left-6 text-xs font-mono tracking-widest text-[#D4AF37]/70">{s.num}</span>
              <span className={`absolute top-5 right-6 text-[9px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 border ${s.enabled ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" : "text-[#94A3B8] border-white/10 bg-white/5"}`} data-testid={`slot-status-${s.key}`}>
                {s.enabled ? "Active" : "Coming Soon"}
              </span>
              <div className={`mt-6 h-16 w-16 rounded-2xl border flex items-center justify-center ${s.enabled ? "border-[#D4AF37]/40 bg-[#D4AF37]/10" : "border-white/10 bg-white/5"}`}>
                <Icon className={`h-7 w-7 ${s.enabled ? "text-[#D4AF37]" : "text-[#64748B]"}`} />
              </div>
              <h3 className={`font-heading text-lg tracking-[0.15em] font-semibold ${s.enabled ? "text-white" : "text-[#94A3B8]"}`}>{s.name}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
              <button
                onClick={(e) => { e.stopPropagation(); if (!s.enabled) return; setSelected(s.key); }}
                disabled={!s.enabled}
                data-testid={`slot-cta-${s.key}`}
                className={s.enabled
                  ? "btn-outline-gold w-full mt-2 px-5 py-3 text-sm rounded-full flex items-center justify-center gap-2"
                  : "w-full mt-2 px-5 py-3 text-sm rounded-full border border-white/10 text-[#64748B] flex items-center justify-center gap-2 cursor-not-allowed"}>
                {s.enabled ? <>{s.cta}<ArrowRight className="h-4 w-4" /></> : <><Lock className="h-3.5 w-3.5" />Coming Soon</>}
              </button>
            </div>
          );
        })}
      </div>
      )}

      {/* Module workspace header — the user always knows which module they are inside */}
      {selected && slotMeta && (
        <div className="flex items-center gap-4 flex-wrap" data-testid="ib-module-header">
          <button onClick={() => setSelected(null)} className="btn-outline-gold px-4 py-2 text-xs rounded-full" data-testid="ib-module-back">← My Activity</button>
          <p className="eyebrow-label" data-testid="ib-module-title">{slotMeta.num} · {slotMeta.name}</p>
        </div>
      )}

      {/* Selected slot panel */}
      {selected && selected !== "signal" && selected !== "resources" && slotMeta && (
        <div className="space-y-4" data-testid={`slot-panel-${selected}`}>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow-label mb-1">{slotMeta.num} · {slotMeta.name}</p>
              <p className="text-xs text-[#64748B]">
                {selected === "live" && "One active LIVE session at a time. Sessions expire automatically at start time + duration (server time)."}
                {selected === "e_video" && "Videos are hosted on your private Telegram — PLUSMAKER stores only the link. Set FREE or a COINS price — only your own clients can access."}
                {selected === "e_book" && "PDF is stored securely in PLUSMAKER storage. Clients download after free access or a COINS purchase."}
                {selected === "webinar" && "FREE bookings are approved by admin; paid bookings are instant. Capacity counts approved bookings only — raising it re-opens slots."}
                {selected === "coaching" && "Clients book with COINS — payment confirms instantly and reserves a slot. Raising capacity re-opens slots automatically."}
              </p>
            </div>
            <button onClick={() => openCreate(selected)} className="btn-gold px-5 py-2.5 text-sm shrink-0" data-testid={`panel-create-${selected}`}>
              + New {slotMeta.name === "LIVE" ? "Live" : slotMeta.name === "E-VIDEO" ? "E-Video" : slotMeta.name === "E-BOOK" ? "E-Book" : slotMeta.name === "WEBINAR" ? "Webinar" : "Coaching"}
            </button>
          </div>
          {byType(SLOT_TYPE[selected] || selected).length === 0 ? (
            <div className="card-lux p-10 text-center" data-testid={`slot-empty-${selected}`}>
              <p className="text-sm text-[#64748B]">Nothing here yet. Create your first {slotMeta.name} activity.</p>
            </div>
          ) : (
            <div className="space-y-3">{byType(SLOT_TYPE[selected] || selected).map(renderRow)}</div>
          )}
        </div>
      )}

      {selected === "resources" && (
        <div className="space-y-4" data-testid="slot-panel-resources">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow-label mb-1">{resTab === "requests" ? "06 · INDICATOR REQUESTS" : "06 · RESOURCES"}</p>
              <p className="text-xs text-[#64748B]">{resTab === "requests"
                ? "Client claims and purchases of your Indicators — their TradingView username is included so you can grant access."
                : "IB-owned Indicators and Expert Advisors — FREE or COINS, with permanent / monthly / weekly validity."}</p>
            </div>
            {resTab !== "requests" && (
              <button onClick={() => openCreate(`resource_${resTab}`)} className="btn-gold px-5 py-2.5 text-sm shrink-0" data-testid="panel-create-resources">
                + New {resTab === "ea" ? "Expert Advisor" : "Indicator"}
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="resources-subnav">
            {[["indicator", "Indicator"], ["ea", "Expert Advisor"], ["requests", "Indicator Requests"]].map(([k, label]) => (
              <button key={k} onClick={() => setResTab(k)} data-testid={`resources-tab-${k}`}
                className={resTab === k
                  ? "px-4 py-2 rounded-full text-xs font-mono tracking-wider border border-[#D4AF37]/60 bg-[#D4AF37]/15 text-[#D4AF37] transition-colors"
                  : "px-4 py-2 rounded-full text-xs font-mono tracking-wider border border-white/10 text-[#94A3B8] hover:border-[#D4AF37]/40 transition-colors"}>
                {label}
              </button>
            ))}
          </div>
          {resTab === "requests" ? (
            !indRequests ? (
              <div className="card-lux p-10 text-center"><p className="text-sm text-[#64748B]">Loading…</p></div>
            ) : indRequests.length === 0 ? (
              <div className="card-lux p-10 text-center" data-testid="slot-empty-requests">
                <p className="text-sm text-[#64748B]">No indicator requests yet.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="requests-list">
                {indRequests.map((rq) => (
                  <div key={rq.id} className="card-lux p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`request-row-${rq.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium text-sm">{rq.client_name}</span>
                        <span className="text-[10px] text-[#475569]">{rq.client_uid}</span>
                        <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${rq.request_type === "coins_purchase" ? "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10" : "text-emerald-400 border-emerald-400/30 bg-emerald-400/10"}`}>{rq.request_type === "coins_purchase" ? `COINS PURCHASE · ${(rq.price_minor / 100).toFixed(2)}` : "FREE CLAIM"}</span>
                        <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${rq.status === "done" ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" : "text-amber-400 border-amber-400/30 bg-amber-400/10"}`} data-testid={`request-status-${rq.id}`}>{rq.status}</span>
                      </div>
                      <p className="text-xs text-[#64748B] mt-1">{rq.indicator_title} · TradingView: <span className="text-[#94A3B8]">{rq.tradingview_username}</span> · {fmtDT(rq.created_at)}</p>
                    </div>
                    <button onClick={() => setReqDetail(rq)} className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded-lg px-4 py-2 transition-colors" data-testid={`request-view-${rq.id}`}>View Details</button>
                  </div>
                ))}
              </div>
            )
          ) : byType(`resource_${resTab}`).length === 0 ? (
            <div className="card-lux p-10 text-center" data-testid="slot-empty-resources">
              <p className="text-sm text-[#64748B]">Nothing here yet. Create your first {resTab === "ea" ? "Expert Advisor" : "Indicator"}.</p>
            </div>
          ) : (
            <div className="space-y-3">{byType(`resource_${resTab}`).map(renderRow)}</div>
          )}
        </div>
      )}

      {/* Indicator Request details (IB-owned) */}
      {reqDetail && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReqDetail(null)}>
          <div className="card-lux w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="request-detail-modal">
            <p className="eyebrow-label mb-1">INDICATOR REQUEST</p>
            <h3 className="font-heading text-xl font-semibold text-white mb-6">{reqDetail.indicator_title}</h3>
            <div className="space-y-3 text-sm">
              {[
                ["Client", `${reqDetail.client_name} (${reqDetail.client_uid})`],
                ["Client Email", reqDetail.client_email || "—"],
                ["Registered Under", "You (owning IB)"],
                ["TradingView Username", reqDetail.tradingview_username || "—"],
                ["Request Type", reqDetail.request_type === "coins_purchase" ? "COINS PURCHASE" : "FREE CLAIM"],
                ["Price", reqDetail.request_type === "coins_purchase" ? `${(reqDetail.price_minor / 100).toFixed(2)} COINS` : "FREE"],
                ["Validity", (reqDetail.validity || "permanent").toUpperCase()],
                ["Requested", fmtDT(reqDetail.created_at)],
              ].map(([lbl, val], i) => (
                <div key={lbl} className="flex items-center justify-between gap-4 border-b border-white/5 pb-2.5" data-testid={`request-detail-${lbl.toLowerCase().replace(/ /g, "-")}`}>
                  <span className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">{lbl}</span>
                  <span className={`text-right ${lbl === "TradingView Username" ? "text-[#D4AF37] font-semibold" : "text-white"}`}>{val}</span>
                </div>
              ))}
              <div className="flex items-center justify-between gap-4 pb-1">
                <span className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Status</span>
                <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${reqDetail.status === "done" ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" : "text-amber-400 border-amber-400/30 bg-amber-400/10"}`} data-testid="request-detail-status">{reqDetail.status}</span>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-7">
              <button onClick={() => setReqDetail(null)} className="btn-outline-gold px-5 py-2.5 text-sm rounded-full" data-testid="request-detail-close">Close</button>
              {reqDetail.status !== "done" && (
                <button onClick={() => markReqDone(reqDetail.id)} className="btn-gold px-5 py-2.5 text-sm rounded-full" data-testid="request-mark-done">Mark as Done</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Webinar / Coaching bookings management (IB-owned approvals) */}
      {bookAct && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBookAct(null)}>
          <div className="card-lux w-full max-w-2xl p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ib-bookings-modal">
            <p className="eyebrow-label mb-1">BOOKINGS</p>
            <p className="text-xs text-[#64748B] mb-6">{bookAct.title} — FREE bookings need your approval; paid bookings confirm instantly.</p>
            {!bookRows ? (
              <p className="text-sm text-[#64748B] text-center py-8">Loading…</p>
            ) : bookRows.length === 0 ? (
              <p className="text-sm text-[#64748B] text-center py-8" data-testid="ib-bookings-empty">No bookings yet.</p>
            ) : (
              <div className="space-y-3" data-testid="ib-bookings-list">
                {bookRows.map((b) => (
                  <div key={b.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-4 space-y-3" data-testid={`ib-booking-row-${b.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm text-white font-medium">{b.member_name} <span className="text-[#475569] text-xs">({b.member_uid})</span></p>
                        <p className="text-[10px] text-[#64748B] mt-0.5">{b.price_minor > 0 ? `${(b.price_minor / 100).toFixed(2)} COINS` : "FREE"} · {fmtDT(b.created_at)}</p>
                        {b.client_phone && <p className="text-xs text-[#94A3B8] mt-0.5" data-testid={`ib-booking-phone-${b.id}`}>Phone: {b.client_phone}</p>}
                      </div>
                      <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${b.status === "approved" ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" : b.status === "rejected" ? "text-red-400 border-red-400/30 bg-red-400/10" : "text-amber-400 border-amber-400/30 bg-amber-400/10"}`} data-testid={`ib-booking-status-${b.id}`}>{b.status}</span>
                    </div>
                    {b.reject_reason && <p className="text-xs text-red-400/80">Reason: {b.reject_reason}</p>}
                    {b.status === "pending" && (
                      rejId === b.id ? (
                        <div className="flex items-center gap-2">
                          <input value={rejReason} onChange={(e) => setRejReason(e.target.value)} placeholder="Rejection reason (required)"
                            className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white placeholder:text-[#475569] focus:border-[#D4AF37]/50 focus:outline-none"
                            data-testid={`ib-booking-reject-reason-${b.id}`} />
                          <button onClick={() => decideBooking(b.id, "reject", rejReason.trim())}
                            className="text-xs text-black bg-red-400 hover:bg-red-300 rounded-lg px-3 py-2 transition-colors"
                            data-testid={`ib-booking-reject-confirm-${b.id}`}>Confirm</button>
                          <button onClick={() => { setRejId(null); setRejReason(""); }}
                            className="text-xs text-[#94A3B8] border border-white/10 rounded-lg px-3 py-2 transition-colors"
                            data-testid={`ib-booking-reject-cancel-${b.id}`}>Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button onClick={() => decideBooking(b.id, "approve")}
                            className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-4 py-2 transition-colors"
                            data-testid={`ib-booking-approve-${b.id}`}>Approve</button>
                          <button onClick={() => { setRejId(b.id); setRejReason(""); }}
                            className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-4 py-2 transition-colors"
                            data-testid={`ib-booking-reject-${b.id}`}>Reject</button>
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={() => setBookAct(null)} className="btn-outline-gold px-5 py-2.5 text-sm rounded-full" data-testid="ib-bookings-close">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* LIVE / E-VIDEO / E-BOOK form modal */}
      {modal && (
        <Modal title={`${modal.id ? "Edit" : "Create"} ${slotMeta?.name || SLOT_TITLES[modal.slot] || modal.slot}`}
          sub="Audience: My Clients — eligible clients receive a push notification on publish."
          onClose={() => setModal(null)} testid="activity-form-modal">
          <div className="space-y-4">
            <Field label="Title">
              <input value={f.title} onChange={(e) => setModal({ ...modal, form: { ...f, title: e.target.value } })}
                className="input-lux text-sm" maxLength={200} data-testid="activity-title-input" />
            </Field>

            {modal.slot === "live" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Start date & time">
                    <input type="datetime-local" value={f.starts_at} onChange={(e) => setModal({ ...modal, form: { ...f, starts_at: e.target.value } })}
                      className="input-lux text-sm" data-testid="live-start-input" />
                  </Field>
                  <Field label="Duration">
                    <select value={f.duration_hours} onChange={(e) => setModal({ ...modal, form: { ...f, duration_hours: e.target.value } })}
                      className="input-lux text-sm" data-testid="live-duration-select">
                      {["0.5", "1", "1.5", "2", "3", "4", "6", "8", "12", "24"].map((h) => (
                        <option key={h} value={h}>{h} hour{h === "1" ? "" : "s"}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Join URL (link only — no video hosting)">
                  <input value={f.live_url} onChange={(e) => setModal({ ...modal, form: { ...f, live_url: e.target.value } })}
                    placeholder="https://" className="input-lux text-sm" data-testid="live-url-input" />
                </Field>
                <div className="flex items-center gap-2 text-xs text-emerald-400">
                  <Radio className="h-3.5 w-3.5" /> Expires automatically at start + duration. Only one active LIVE at a time.
                </div>
              </>
            )}

            {modal.slot === "e_video" && (
              <>
                <Field label="Description / education notes">
                  <textarea value={f.content} onChange={(e) => setModal({ ...modal, form: { ...f, content: e.target.value } })}
                    rows={3} className="input-lux text-sm" data-testid="evideo-content-input" />
                </Field>
                <Field label="Private Telegram link">
                  <input value={f.external_url} onChange={(e) => setModal({ ...modal, form: { ...f, external_url: e.target.value } })}
                    placeholder="https://t.me/…" className="input-lux text-sm" data-testid="evideo-link-input" />
                </Field>
                <Field label={`Cover image / thumbnail${f.media_url ? " — attached" : ""}`}>
                  <label className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors inline-flex items-center gap-1.5 cursor-pointer" data-testid="evideo-image-upload">
                    <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload cover image"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadImg(e.target.files?.[0])} />
                  </label>
                </Field>
              </>
            )}

            {modal.slot === "e_book" && (
              <>
                <Field label="Description / education notes">
                  <textarea value={f.content} onChange={(e) => setModal({ ...modal, form: { ...f, content: e.target.value } })}
                    rows={3} className="input-lux text-sm" data-testid="ebook-content-input" />
                </Field>
                <Field label="PDF file (max 15 MB)">
                  <div className="flex items-center gap-3">
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors flex items-center gap-1.5"
                      data-testid="ebook-upload-button">
                      <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload PDF"}
                    </button>
                    <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(e.target.files?.[0])} />
                    {(f.file_name || modal.id) && <span className="text-xs text-[#94A3B8] flex items-center gap-1.5" data-testid="ebook-file-name"><FileText className="h-3.5 w-3.5 text-[#D4AF37]" />{f.file_name || "PDF attached"}</span>}
                  </div>
                </Field>
                <Field label={`Cover image${f.media_url ? " — attached" : ""}`}>
                  <label className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors inline-flex items-center gap-1.5 cursor-pointer" data-testid="ebook-image-upload">
                    <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload cover image"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadImg(e.target.files?.[0])} />
                  </label>
                </Field>
              </>
            )}

            {(modal.slot === "e_video" || modal.slot === "e_book") && (
              <>
                <Field label="Access">
                  <div className="flex gap-2" data-testid="access-picker">
                    {[["free", "FREE"], ["coin", "PAID · COINS"]].map(([v, label]) => (
                      <button type="button" key={v} data-testid={`access-${v}`}
                        onClick={() => setModal({ ...modal, form: { ...f, access_type: v } })}
                        className={f.access_type === v
                          ? "px-4 py-2 rounded-full text-xs font-mono tracking-wider border border-[#D4AF37]/60 bg-[#D4AF37]/15 text-[#D4AF37] transition-colors"
                          : "px-4 py-2 rounded-full text-xs font-mono tracking-wider border border-white/10 text-[#94A3B8] hover:border-[#D4AF37]/40 transition-colors"}>
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>
                {f.access_type === "coin" && (
                  <Field label="COINS price (1 COINS = USD 1)">
                    <input type="number" min="1" step="0.01" value={f.coin_price}
                      onChange={(e) => setModal({ ...modal, form: { ...f, coin_price: e.target.value } })}
                      placeholder="e.g. 100" className="input-lux text-sm font-mono" data-testid="coin-price-input" />
                  </Field>
                )}
                <Field label="Expiry date (optional)">
                  <input type="date" value={f.expires_at} onChange={(e) => setModal({ ...modal, form: { ...f, expires_at: e.target.value } })}
                    className="input-lux text-sm" data-testid="activity-expiry-input" />
                </Field>
              </>
            )}

            {modal.slot === "webinar" && (
              <>
                <Field label="Description / Details">
                  <textarea value={f.content} onChange={(e) => setModal({ ...modal, form: { ...f, content: e.target.value } })}
                    rows={3} className="input-lux text-sm" data-testid="webinar-content-input" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Date">
                    <input type="date" value={f.event_date} onChange={(e) => setModal({ ...modal, form: { ...f, event_date: e.target.value } })}
                      className="input-lux text-sm" data-testid="webinar-date-input" />
                  </Field>
                  <Field label="Time">
                    <input type="time" value={f.event_time} onChange={(e) => setModal({ ...modal, form: { ...f, event_time: e.target.value } })}
                      className="input-lux text-sm" data-testid="webinar-time-input" />
                  </Field>
                </div>
                <Field label={`Poster / Image${f.media_url ? " — attached" : ""}`}>
                  <label className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors inline-flex items-center gap-1.5 cursor-pointer" data-testid="webinar-image-upload">
                    <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadImg(e.target.files?.[0])} />
                  </label>
                </Field>
                <Field label="Student Capacity">
                  <input type="number" min="1" step="1" value={f.capacity} onChange={(e) => setModal({ ...modal, form: { ...f, capacity: e.target.value } })}
                    placeholder="e.g. 50" className="input-lux text-sm font-mono" data-testid="webinar-capacity-input" />
                </Field>
                <AccessPicker f={f} setModal={setModal} />
              </>
            )}

            {modal.slot === "coaching" && (
              <>
                <Field label="Description / Details">
                  <textarea value={f.content} onChange={(e) => setModal({ ...modal, form: { ...f, content: e.target.value } })}
                    rows={3} className="input-lux text-sm" data-testid="coaching-content-input" />
                </Field>
                <Field label={`Image${f.media_url ? " — attached" : ""}`}>
                  <label className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors inline-flex items-center gap-1.5 cursor-pointer" data-testid="coaching-image-upload">
                    <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadImg(e.target.files?.[0])} />
                  </label>
                </Field>
                <Field label="Location">
                  <input value={f.location} onChange={(e) => setModal({ ...modal, form: { ...f, location: e.target.value } })}
                    placeholder="e.g. Kuala Lumpur / Zoom" className="input-lux text-sm" data-testid="coaching-location-input" />
                </Field>
                <Field label="Availability (date / time)">
                  <input value={f.availability} onChange={(e) => setModal({ ...modal, form: { ...f, availability: e.target.value } })}
                    placeholder="e.g. Saturday 27 Sept, 2:00 PM" className="input-lux text-sm" data-testid="coaching-availability-input" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Capacity">
                    <input type="number" min="1" step="1" value={f.capacity} onChange={(e) => setModal({ ...modal, form: { ...f, capacity: e.target.value } })}
                      placeholder="e.g. 5" className="input-lux text-sm font-mono" data-testid="coaching-capacity-input" />
                  </Field>
                  <Field label="COINS price (1 COINS = USD 1)">
                    <input type="number" min="1" step="0.01" value={f.coin_price} onChange={(e) => setModal({ ...modal, form: { ...f, coin_price: e.target.value } })}
                      placeholder="e.g. 200" className="input-lux text-sm font-mono" data-testid="coaching-price-input" />
                  </Field>
                </div>
              </>
            )}

            {(modal.slot === "resource_indicator" || modal.slot === "resource_ea") && (
              <>
                <Field label="Description">
                  <textarea value={f.content} onChange={(e) => setModal({ ...modal, form: { ...f, content: e.target.value } })}
                    rows={3} className="input-lux text-sm" data-testid="resource-content-input" />
                </Field>
                <Field label={`Image${f.media_url ? " — attached" : ""}`}>
                  <label className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors inline-flex items-center gap-1.5 cursor-pointer" data-testid="resource-image-upload">
                    <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadImg(e.target.files?.[0])} />
                  </label>
                </Field>
                <Field label="Validity / Access Duration">
                  <select value={f.validity} onChange={(e) => setModal({ ...modal, form: { ...f, validity: e.target.value } })}
                    className="input-lux text-sm" data-testid="resource-validity-select">
                    <option value="permanent">Permanent</option>
                    <option value="monthly">Monthly (30 days)</option>
                    <option value="weekly">Weekly (7 days)</option>
                  </select>
                </Field>
                <Field label="Tutorial / Installation URL">
                  <input value={f.tutorial_url} onChange={(e) => setModal({ ...modal, form: { ...f, tutorial_url: e.target.value } })}
                    placeholder="https://t.me/…" className="input-lux text-sm" data-testid="resource-tutorial-input" />
                </Field>
                <Field label="Requirements / Conditions">
                  <textarea value={f.requirements} onChange={(e) => setModal({ ...modal, form: { ...f, requirements: e.target.value } })}
                    rows={2} className="input-lux text-sm resize-none" placeholder="e.g. MT5 account required" data-testid="resource-requirements-input" />
                </Field>
                {modal.slot === "resource_ea" && (
                  <Field label={`EA File (EX4/EX5/MQ4/MQ5/ZIP)${f.file_name ? ` — current: ${f.file_name}` : ""}`}>
                    <label className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded px-3 py-2 transition-colors inline-flex items-center gap-1.5 cursor-pointer" data-testid="ea-file-upload">
                      <Upload className="h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload EA file"}
                      <input type="file" accept=".ex4,.ex5,.mq4,.mq5,.zip" className="hidden" disabled={uploading} onChange={(e) => uploadEa(e.target.files?.[0])} />
                    </label>
                  </Field>
                )}
                <AccessPicker f={f} setModal={setModal} />
              </>
            )}

            {!modal.id && (
              <label className="flex items-center gap-2 text-sm text-[#94A3B8] cursor-pointer">
                <input type="checkbox" checked={f.publish} onChange={(e) => setModal({ ...modal, form: { ...f, publish: e.target.checked } })}
                  className="accent-[#D4AF37]" data-testid="activity-publish-checkbox" />
                Publish immediately (eligible clients get a push notification)
              </label>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-8">
            <button onClick={() => setModal(null)} className="px-5 py-2.5 text-sm text-[#94A3B8] hover:text-white transition-colors" data-testid="activity-cancel-button">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-gold px-6 py-2.5 text-sm" data-testid="activity-save-button">
              {saving ? "Saving…" : modal.id ? "Save Changes" : f.publish ? "Publish" : "Save Draft"}
            </button>
          </div>
        </Modal>
      )}

      {/* TOPIC creation removed — Topics is a single direct-entry broadcast (no picker / no create). */}
    </div>
  );
}
